<template>
  <div v-touch-pan.horizontal="swipeHandler" :style="swipeStyle" class="recruitment-candidate-card">
    <header>
      <QBtn v-if="canIgnore" class="btn-left" icon="far fa-xmark" flat size="20px" @click="ignoreCandidate" />
      <Avatar :picture="candidate.getPicture()" size="115px" @click="goToProfile" />
      <span v-if="candidate.isFavorite()" class="recruitment-candidate-card-status"><QIcon name="favorite" /></span>
      <QBtn
        v-if="canContact && !candidate.isIgnored()"
        class="btn-right q-ma-sm"
        icon-right="report_problem"
        flat
        dense
        color="negative"
        :label="$t('ETAB.PAGES.RECRUITMENT.@REPORT_CANDIDATE')"
        no-caps
        @click="openReportCandidateModal"
      />
    </header>

    <article class="relative-position" @click="goToProfile">
      <div v-if="candidate.hasRefused()" class="recruitment-candidate-card_overlay">
        <h3>{{ $t("ETAB.PAGES.EXTRA.@REFUSED_EXTRA") }}</h3>
      </div>
      <!-- Identity -->
      <section class="recruitment-candidate-card-identity">
        <span class="recruitment-candidate-card-name">{{ candidate.getFirstname() }}</span>
        <span v-if="candidate.getPostalCode()" class="recruitment-candidate-card-postal-code">({{ candidate.getPostalCode() }})</span>
      </section>
      <!-- Experience -->
      <section>
        <span class="recruitment-candidate-card-jobs">
          {{ $tc("ETAB.PAGES.CANDIDATE.CARD.@JOBS", candidate.getExperiencesAmount()) }}
        </span>
        <template v-if="candidate.getAverageNote() > 0">
          <span class="recruitment-candidate-card-separator" />
          <StarsNotation :note="candidate.getAverageNote()" :notes-amount="candidate.getNotesAmount()" />
        </template>
        <template v-if="totalReports">
          <span class="recruitment-candidate-card-separator" />
          <span class="recruitment-candidate-card-jobs">
            <QIcon name="fad fa-triangle-exclamation" color="red" size="xs" class="q-mr-xs q-mr-xs" />
            {{ $t("ETAB.PAGES.CANDIDATE.CARD.@REPORTS_SHORT", { count: totalReports }) }}
          </span>
        </template>
      </section>
      <!-- Tag --->
      <section class="q-mt-md">
        <div class="text-right">
          <QChip v-if="candidate.isNovice()" color="primary" text-color="white" :label="$t('ETAB.PAGES.CANDIDATE.CARD.@NOVICE')" />
        </div>
      </section>
      <!-- Top Recommendation -->
      <section v-if="showExperience">
        <TopRecommendation :recommendation="candidate.getRecommendation()" />
      </section>
    </article>
    <footer class="recruitment-candidate-card-contact">
      <ExtraBtnColumn flat no-caps color="white" icon="icon-f-eye" :label="$t('ETAB.PAGES.CANDIDATE.CARD.@GO_TO_PROFILE')" @click="goToProfile" />
      <ExtraBtnColumn
        flat
        no-caps
        color="white"
        icon="icon-extra-contact"
        :label="$t('ETAB.PAGES.CANDIDATE.CARD.@CONTACT')"
        @click="contactCandidate"
      />
    </footer>
  </div>
</template>

<script setup>
  import { PermanentCandidateModel, RecruitmentDefaultModel } from "@core/models";
  import StarsNotation from "@core/components/common/StarsNotation.component.vue";
  import TopRecommendation from "@etab/components/candidates/TopRecommendation.component.vue";
  import { reactive, computed, defineEmits } from "vue";

  const SWIPE_DISTANCE = 175;
  const emit = defineEmits(["ignoreCandidate", "goToProfile", "onContactCandidate", "reportCandidate", "openReportModal"]);

  const swipeStyle = reactive({});
  const swipeTimer = reactive(undefined);

  const props = defineProps({
    openReportModal: {
      type: Function,
      default: () => false,
    },
    candidate: {
      type: Object,
      default: () => new PermanentCandidateModel(),
    },
    recruitment: {
      type: Object,
      default: () => new RecruitmentDefaultModel(),
    },
    canContact: {
      type: Boolean,
      default: false,
    },
    canIgnore: {
      type: Boolean,
      default: false,
    },
    showExperience: {
      type: Boolean,
      default: false,
    },
  });

  const totalReports = computed(() => {
    return props.candidate.getCandidateReports()?.getTotalReports();
  });

  const swipeHandler = function (swipe) {
    // we can swipe left only
    if (props.canIgnore && swipe.direction === "left") {
      clearTimeout(swipeTimer);
      const distance = swipe.distance.x;
      if (distance > 5) {
        swipeStyle = { transform: `translate(${-distance}px)` };
      }
      // we apply action only at the end of the swipe (when the user put his finger up)
      if (swipe.isFinal) {
        // after an arbitrary distance, we consider the user wanted to act on props.candidate
        if (distance > SWIPE_DISTANCE) {
          ignoreCandidate();
          swipeStyle = { transform: `translate(${-1000}px)` };
        } else {
          // we reset the UI at the end of the action
          swipeTimer = setTimeout(() => {
            swipeStyle = { transform: `translate(0px)` };
          }, 250);
        }
      }
    }
  };

  const ignoreCandidate = function () {
    emit("ignoreCandidate", { candidate: props.candidate });
  };

  const goToProfile = function () {
    emit("goToProfile", { candidate: props.candidate });
  };

  const contactCandidate = function () {
    emit("onContactCandidate", { candidate: props.candidate });
  };

  const reportCandidate = function () {
    emit("reportCandidate", { candidate: props.candidate });
  };

  const openReportCandidateModal = function () {
    emit("openReportModal", { candidate: props.candidate });
  };
</script>

<style lang="scss" scoped>
  .recruitment-candidate-card {
    header {
      z-index: 2;
    }
    &_overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba($primary, 0.55);
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      backdrop-filter: blur(2px);
      z-index: 1;

      h3 {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        color: white;
        line-height: 1.2;
        flex: 1;
        font-size: 1.2em;
      }
    }
  }
</style>
